import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  completeDocumentUpload,
  createDocumentUploadUrl,
  deleteDocument,
  putDocumentBytes,
  MAX_UPLOAD_BYTES,
  type DocumentType,
} from '@/lib/api/endpoints/documents'
import type { KbSyncFile } from '@/lib/api/endpoints/ai-research'
import { checkFile, contentTypeFor } from '@/lib/documents/accepted-types'
import { ApiErrorCode, isApiError, isForbiddenError } from '@/lib/api/errors'

export type UploadPhase =
  'queued' | 'rejected' | 'reserving' | 'uploading' | 'verifying' | 'done' | 'failed'

export interface UploadItem {
  readonly id: string
  readonly fileName: string
  readonly sizeBytes: number
  readonly phase: UploadPhase
  readonly progress: number
  readonly error: string | null
}

export const DISMISS_DONE_AFTER_MS = 1_200

export const SETTLED: ReadonlySet<UploadPhase> = new Set<UploadPhase>([
  'done',
  'rejected',
  'failed',
])

export interface UploadOutcome {
  uploaded: number
  indexed: readonly KbSyncFile[]
}

export interface StartUploadsInput {
  companyId: string
  files: readonly File[]
  documentType?: DocumentType | undefined
  periodCovered?: string | undefined
}

export interface UseUploadQueue {
  items: readonly UploadItem[]
  isUploading: boolean

  start: (input: StartUploadsInput) => Promise<UploadOutcome>
  clearFinished: () => void
}

function messageFor(error: unknown): string {
  if (isForbiddenError(error)) return 'Your role cannot upload documents for this company.'

  if (isApiError(error)) {
    switch (error.code) {
      case ApiErrorCode.FILE_TOO_LARGE:
        return 'That file is over the 100 MB limit.'
      case 'UNSUPPORTED_FILE_TYPE':
        return error.message
      case 'UPLOAD_VERIFICATION_FAILED':
        return 'The file did not persist to storage. This is usually transient — try again.'
      case ApiErrorCode.NOT_FOUND:
        return 'That company no longer exists.'
      default:
        return error.message
    }
  }

  return error instanceof Error ? error.message : 'Upload failed.'
}

async function commitAfterFailedTransfer(documentId: string): Promise<void> {
  await completeDocumentUpload(documentId).catch(() => undefined)
}

export function useUploadQueue(): UseUploadQueue {
  const queryClient = useQueryClient()
  const [items, setItems] = useState<readonly UploadItem[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const counter = useRef(0)

  const dismissTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  useEffect(() => {
    const timers = dismissTimers.current
    return () => {
      for (const timer of timers.values()) clearTimeout(timer)
      timers.clear()
    }
  }, [])

  const dismissLater = useCallback((id: string) => {
    const timers = dismissTimers.current
    if (timers.has(id)) return

    timers.set(
      id,
      setTimeout(() => {
        timers.delete(id)
        setItems((current) => current.filter((item) => item.id !== id))
      }, DISMISS_DONE_AFTER_MS),
    )
  }, [])

  const patch = useCallback((id: string, changes: Partial<UploadItem>) => {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...changes } : item)))
  }, [])

  const start = useCallback(
    async ({ companyId, files, documentType, periodCovered }: StartUploadsInput) => {
      const queued: UploadItem[] = files.map((file) => {
        counter.current += 1
        const verdict = checkFile(file, MAX_UPLOAD_BYTES)

        return {
          id: `upload-${String(counter.current)}`,
          fileName: file.name,
          sizeBytes: file.size,
          phase: verdict.kind === 'accepted' ? 'queued' : 'rejected',
          progress: 0,
          error: verdict.kind === 'accepted' ? null : verdict.message,
        }
      })

      setItems((current) => [...current, ...queued])

      const accepted = queued
        .map((item, index) => ({ item, file: files[index] }))
        .filter(
          (entry): entry is { item: UploadItem; file: File } =>
            entry.item.phase === 'queued' && entry.file !== undefined,
        )

      if (accepted.length === 0) return { uploaded: 0, indexed: [] }

      setIsUploading(true)
      const indexed: KbSyncFile[] = []
      let uploaded = 0

      for (const { item, file } of accepted) {
        let documentId: string | null = null
        let committing = false

        try {
          patch(item.id, { phase: 'reserving' })

          const reservation = await createDocumentUploadUrl({
            companyId,
            fileName: file.name,
            contentType: contentTypeFor(file),
            sizeBytes: file.size,
            documentType,
            periodCovered,
          })
          documentId = reservation.documentId

          patch(item.id, { phase: 'uploading', progress: 0 })
          await putDocumentBytes(reservation, file, {
            onProgress: (fraction) => {
              patch(item.id, { progress: fraction })
            },
          })

          committing = true

          patch(item.id, { phase: 'verifying', progress: 1 })
          await completeDocumentUpload(reservation.documentId)

          patch(item.id, { phase: 'done', progress: 1, error: null })
          dismissLater(item.id)
          uploaded += 1

          if (reservation.s3Key != null && reservation.s3Key !== '') {
            indexed.push({
              id: reservation.documentId,
              file_name: file.name,
              s3: reservation.s3Key,
            })
          }
        } catch (error) {
          patch(item.id, { phase: 'failed', error: messageFor(error) })

          if (documentId !== null && !committing) {
            await commitAfterFailedTransfer(documentId)
          }
        }
      }

      setIsUploading(false)

      void queryClient.invalidateQueries({ queryKey: ['documents'] })
      void queryClient.invalidateQueries({ queryKey: ['companies'] })
      void queryClient.invalidateQueries({ queryKey: ['company'] })

      return { uploaded, indexed }
    },
    [patch, dismissLater, queryClient],
  )

  const clearFinished = useCallback(() => {
    const timers = dismissTimers.current

    setItems((current) => {
      for (const item of current) {
        if (!SETTLED.has(item.phase)) continue

        const timer = timers.get(item.id)
        if (timer === undefined) continue

        clearTimeout(timer)
        timers.delete(item.id)
      }

      return current.filter((item) => !SETTLED.has(item.phase))
    })
  }, [])

  return { items, isUploading, start, clearFinished }
}

export function useDeleteDocument(): UseMutationResult<{ success: boolean }, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (documentId: string) => deleteDocument(documentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}
